// ─────────────────────────────────────────────────────────────────────────────
// CornerOrb — the compact 128px Rexy call control. Detachable and draggable.
//
// Per Ghost Flag 3 + FIX 1: this consumes the shared mic/warmth/active values
// via useAppShellLive() — no parallel useMicMeter, no parallel socket lifecycle.
// AppShell is the single owner.
//
// S4 — THE WRAPPER OWNS INTERACTION, THE RENDERER NEVER DOES.
//   RexyOrbThree stays pointerEvents:'none'. Everything here — pointer capture,
//   drag, tap-to-call, persistence, viewport clamping — lives in this wrapper.
//   That keeps ONE reusable Three.js component with no interaction concerns.
//
//   Tap vs drag is decided on POINTER UP, never on pointer down, and never
//   after the drag threshold has been crossed. Mis-reading a reposition as a
//   tap would hang up a live call, so the gesture must prove it is not a drag
//   before it is allowed to be a tap.
//
//   Drag moves a fixed-position wrapper by transform:translate3d, mutated
//   directly on the DOM node inside one rAF — no React render per pointermove.
//   Position persists in localStorage so the control is where it was left,
//   across route changes, remounts, refreshes and both CornerOrb mount sites.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef } from 'react';
import RexyOrbThree from '../RexyOrbThree';
import { useAppShellLive } from './AppShellContext';

// S1.1: 64 rejected as too small to read. 128 → 160 (owner ruling 2026-08-01):
// still too small on the phone surface. SIZING only — no gesture threshold moves.
const CORNER_SIZE = 160;

// Distinct thresholds per input type — a finger wanders more than a mouse.
const MOUSE_DRAG_THRESHOLD_PX = 5;
const TOUCH_DRAG_THRESHOLD_PX = 10;
const PEN_DRAG_THRESHOLD_PX = 8;

const CONTROL_GUTTER = 12;
const DEFAULT_RIGHT = 16;
const DEFAULT_TOP = 88; // clear of the application header
const POSITION_STORAGE_KEY = 'rexy.compact-call-control.position.v1';

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function clampToViewport(x: number, y: number): { x: number; y: number } {
  const maxX = Math.max(CONTROL_GUTTER, window.innerWidth - CORNER_SIZE - CONTROL_GUTTER);
  const maxY = Math.max(CONTROL_GUTTER, window.innerHeight - CORNER_SIZE - CONTROL_GUTTER);
  return { x: clamp(x, CONTROL_GUTTER, maxX), y: clamp(y, CONTROL_GUTTER, maxY) };
}

function defaultPosition(): { x: number; y: number } {
  return clampToViewport(window.innerWidth - CORNER_SIZE - DEFAULT_RIGHT, DEFAULT_TOP);
}

/** Read synchronously at mount so the control never flashes at the default
 *  position before jumping to the saved one. Malformed data falls back. */
function loadPosition(): { x: number; y: number } {
  try {
    const raw = window.localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return defaultPosition();
    const parsed = JSON.parse(raw);
    const x = Number(parsed?.x);
    const y = Number(parsed?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return defaultPosition();
    return clampToViewport(x, y);
  } catch {
    return defaultPosition();
  }
}

function savePosition(p: { x: number; y: number }) {
  try {
    window.localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: p.x, y: p.y }));
  } catch {
    /* storage disabled/full — the control still works, it just won't persist */
  }
}

export default function CornerOrb() {
  const { warmth, active, userAmplitude, agentAmplitude, ptt } = useAppShellLive();

  const nodeRef = useRef<HTMLDivElement | null>(null);
  const posRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const rafRef = useRef(0);

  // Gesture state — refs only, so pointermove never triggers a React render.
  const pointerIdRef = useRef<number | null>(null);
  const startRef = useRef({ px: 0, py: 0, ox: 0, oy: 0, threshold: MOUSE_DRAG_THRESHOLD_PX });
  const draggingRef = useRef(false);

  const paint = useCallback(() => {
    rafRef.current = 0;
    const el = nodeRef.current;
    if (el) el.style.transform = `translate3d(${posRef.current.x}px, ${posRef.current.y}px, 0)`;
  }, []);

  const schedulePaint = useCallback(() => {
    if (!rafRef.current) rafRef.current = requestAnimationFrame(paint);
  }, [paint]);

  // Mount: adopt the stored position immediately.
  useEffect(() => {
    posRef.current = loadPosition();
    paint();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [paint]);

  // Resize / orientation change: re-clamp so it can never be stranded off-screen.
  useEffect(() => {
    const onResize = () => {
      posRef.current = clampToViewport(posRef.current.x, posRef.current.y);
      schedulePaint();
      savePosition(posRef.current);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [schedulePaint]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== null) return; // one gesture at a time
    pointerIdRef.current = e.pointerId;
    draggingRef.current = false;
    startRef.current = {
      px: e.clientX,
      py: e.clientY,
      ox: posRef.current.x,
      oy: posRef.current.y,
      threshold:
        e.pointerType === 'touch'
          ? TOUCH_DRAG_THRESHOLD_PX
          : e.pointerType === 'pen'
            ? PEN_DRAG_THRESHOLD_PX
            : MOUSE_DRAG_THRESHOLD_PX,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture unavailable — move/up still arrive on the element */
    }
    e.preventDefault();
    e.stopPropagation();
    // NO call action here. The tap is decided on pointer UP.
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (pointerIdRef.current !== e.pointerId) return;
      const s = startRef.current;
      const dx = e.clientX - s.px;
      const dy = e.clientY - s.py;
      if (!draggingRef.current) {
        if (Math.hypot(dx, dy) < s.threshold) return; // still a candidate tap
        draggingRef.current = true; // past the threshold: can never become a tap
      }
      posRef.current = clampToViewport(s.ox + dx, s.oy + dy);
      schedulePaint();
      e.preventDefault();
      e.stopPropagation();
    },
    [schedulePaint],
  );

  const endGesture = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, cancelled: boolean) => {
      if (pointerIdRef.current !== e.pointerId) return;
      pointerIdRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      if (cancelled) return; // cancellation NEVER calls PTT
      if (wasDragging) {
        savePosition(posRef.current); // persist only on a completed drag
        return;
      }
      ptt(); // the one and only call action, exactly once
    },
    [ptt],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => endGesture(e, false),
    [endGesture],
  );
  const onPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => endGesture(e, true),
    [endGesture],
  );

  return (
    <div
      ref={nodeRef}
      style={wrapperStyle}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      role="button"
      aria-label="Rexy — tap to talk, drag to move"
      title="Tap to talk to Rexy · drag to move"
    >
      <RexyOrbThree
        size={CORNER_SIZE}
        userAmplitude={userAmplitude}
        agentAmplitude={agentAmplitude}
        warmth={warmth}
        sessionActive={active}
        // Compact control: the first REXY_PRIMARY_COUNT — the complete identity
        // set. The SAME count in a call; only the formation changes. (That count
        // is 3072 as of 2026-08-01; it is stated once, in rexySilhouetteTargets,
        // and never duplicated as a literal here.)
        idleFormation="rexy"
        silhouetteConfiguration="compact"
      />
    </div>
  );
}

const wrapperStyle: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  top: 0,
  width: CORNER_SIZE,
  height: CORNER_SIZE,
  zIndex: 11,
  cursor: 'grab',
  touchAction: 'none',
  userSelect: 'none',
  WebkitUserSelect: 'none',
};
